import { useCallback, useState } from 'react';

import { useAnalytics } from 'src/components/settings/analytics';

import { useWorkspaceAiPreferences } from './atlassian-intelligence';

import { WorkspaceAiPreferenceResourceDataType } from '.';

export enum OptInStatus {
  Enabled = 'enabled',
  Disabled = 'disabled',
}

function getPreferenceStateFrom({
  action,
  previous,
  current,
}: {
  action: string;
  previous: boolean;
  current: boolean;
}) {
  return {
    action,
    attributes: {
      state: parseOptInStatus(current),
      prevState: parseOptInStatus(previous),
    },
  } as const;
}

function parseOptInStatus(value: boolean) {
  if (value) {
    return OptInStatus.Enabled;
  } else {
    return OptInStatus.Disabled;
  }
}

function getStateFrom(preference?: WorkspaceAiPreferenceResourceDataType) {
  if (!preference) {
    return undefined;
  }
  return {
    status: parseOptInStatus(preference.aiEnabled),
    preferences: {
      rovoChatEnabled: preference.aiRovoChatEnabled,
      aiEditorFeatures: preference.aiEditorFeaturesEnabled,
    },
  };
}

export function useAnalyticsEvents() {
  const { publishScreenEvent, publishTrackEvent } = useAnalytics();

  const publishToggleTrackEvt = useCallback(
    (
      status: OptInStatus,
      previous?: WorkspaceAiPreferenceResourceDataType,
      current?: WorkspaceAiPreferenceResourceDataType
    ) => {
      const state = current
        ? getStateFrom(current)
        : {
            status,
          };

      publishTrackEvent({
        action: 'toggleAIOptInStatus',
        attributes: {
          ...state,
          prevState: getStateFrom(previous),
        },
      });
    },
    [publishTrackEvent]
  );

  const publishRovoChatPreferenceToggle = useCallback(
    (
      previous: WorkspaceAiPreferenceResourceDataType,
      current: WorkspaceAiPreferenceResourceDataType
    ) => {
      publishTrackEvent(
        getPreferenceStateFrom({
          action: 'toggleAiRovoChat',
          previous: previous.aiRovoChatEnabled,
          current: current.aiRovoChatEnabled,
        })
      );
    },
    [publishTrackEvent]
  );

  const publishAiWritingAssistantTrackEvent = useCallback(
    (
      previous: WorkspaceAiPreferenceResourceDataType,
      current: WorkspaceAiPreferenceResourceDataType
    ) => {
      publishTrackEvent(
        getPreferenceStateFrom({
          action: 'toggleAiWritingAssistant',
          previous: previous.aiEditorFeaturesEnabled,
          current: current.aiEditorFeaturesEnabled,
        })
      );
    },
    [publishTrackEvent]
  );

  const [isPageViewed, setIsPageViewed] = useState(false);
  const preferences = useWorkspaceAiPreferences();

  return {
    onPageViewed: () => {
      const getScreenEventInput = () => {
        return {
          attributes: getStateFrom(preferences),
        };
      };

      if (isPageViewed) {
        return;
      }
      publishScreenEvent(getScreenEventInput());
      setIsPageViewed(true);
    },
    onAiEnabled: (
      previous?: WorkspaceAiPreferenceResourceDataType,
      current?: WorkspaceAiPreferenceResourceDataType
    ) => publishToggleTrackEvt(OptInStatus.Enabled, previous, current),
    onAiDisabled: (
      previous?: WorkspaceAiPreferenceResourceDataType,
      current?: WorkspaceAiPreferenceResourceDataType
    ) => publishToggleTrackEvt(OptInStatus.Disabled, previous, current),
    onAiRovoChatToggleTrackEvent: publishRovoChatPreferenceToggle,
    onAiWritingAssistantToggleTrackEvent: publishAiWritingAssistantTrackEvent,
  };
}
